import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ApplicationPage,
  ApplicationSectionHeader,
  ApplicationStateRegion,
  ApplicationSurface,
  ApplicationViewHeader,
} from "../../../components/application";
import { useAuth } from "../../../auth/authContext";
import {
  completeAssignmentAttempt,
  startOrResumeAssignment,
} from "../services/assignmentApi";
import { TaskRuntimeProvider } from "../../engine/context/TaskRuntimeContext";
import { resolveAssignmentToolComponent } from "../../engine/toolRegistry";
import {
  participantAssignmentErrorMessage,
  targetLabel,
} from "./assignmentViewUtils";
import "./assignmentPresentation.css";

export default function AssignedEvaluationRuntimePage() {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const apiUrl =
    process.env.REACT_APP_API_URL ||
    "http://localhost:5001/api";

  const [runtime, setRuntime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    async function loadRuntime() {
      try {
        setLoading(true);
        const started =
          await startOrResumeAssignment(assignmentId);

        if (active) {
          setRuntime(started);
          setError("");
        }
      } catch (_loadError) {
        if (active) {
          const errorCode =
            _loadError?.response?.data?.error;

          if (errorCode === "ASSIGNMENT_ATTEMPT_COMPLETED") {
            setError(
              participantAssignmentErrorMessage(
                _loadError,
                "This assignment is already complete."
              )
            );
          } else if (errorCode === "ASSIGNMENT_ATTEMPT_SUBMITTED") {
            setError(
              participantAssignmentErrorMessage(
                _loadError,
                "Your responses have been submitted. Return to the assignment details to finish the assignment."
              )
            );
          } else if (
            errorCode ===
            "EVALUATION_TASK_NOT_SUPPORTED_FOR_ASSIGNMENT"
          ) {
            setError(
              participantAssignmentErrorMessage(
                _loadError,
                "This assigned activity is not supported by the current assigned-work runtime."
              )
            );
          } else {
            setError(
              participantAssignmentErrorMessage(
                _loadError,
                "This assignment cannot be opened right now."
              )
            );
          }
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadRuntime();

    return () => {
      active = false;
    };
  }, [assignmentId]);

  async function completeCurrentStep() {
    try {
      await completeAssignmentAttempt(assignmentId);
      navigate(`/app/assigned-work/${assignmentId}`);
    } catch (_completionError) {
      setError(
        participantAssignmentErrorMessage(
          _completionError,
          "This assignment could not be finished right now."
        )
      );
      throw _completionError;
    }
  }

  if (loading) {
    return (
      <ApplicationPage variant="workspace">
        <ApplicationViewHeader
          contextLabel="Assigned Work"
          title="Opening assignment..."
        />
      </ApplicationPage>
    );
  }

  if (error) {
    return (
      <ApplicationPage variant="workspace">
        <ApplicationViewHeader
          contextLabel="Assigned Work"
          title="Assignment unavailable"
        />
        <ApplicationStateRegion
          status="error"
          message={error}
        />
        <p>
          <Link to={`/app/assigned-work/${assignmentId}`}>
            Back to assignment details
          </Link>
          {" · "}
          <Link to="/app/assigned-work">
            Back to Assigned Work
          </Link>
        </p>
      </ApplicationPage>
    );
  }

  const ToolComponent = resolveAssignmentToolComponent(runtime);

  if (!ToolComponent) {
    return (
      <ApplicationPage variant="workspace">
        <ApplicationViewHeader
          contextLabel="Assigned Work"
          title="Unsupported activity"
        />
        <ApplicationStateRegion
          status="warning"
          message="This assigned activity is not supported by the current assigned-work runtime."
        />
        <p>
          <Link to={`/app/assigned-work/${assignmentId}`}>
            Back to assignment details
          </Link>
        </p>
      </ApplicationPage>
    );
  }

  const step = {
    key: runtime.assignment?.target?.taskKey || "assigned-task",
    title:
      runtime.assignment?.title ||
      targetLabel(runtime.assignment?.target) ||
      "Assigned activity",
    instructions:
      runtime.assignment?.instructions || "",
    countsForCompletion: true,
    useCase: "classroom",
  };

  return (
    <ApplicationPage variant="workspace">
      <ApplicationViewHeader
        contextLabel="Assigned Work"
        title={runtime.assignment?.title || "Assignment"}
        description={`Activity: ${targetLabel(runtime.assignment?.target)}`}
        backNavigation={<Link to={`/app/assigned-work/${assignmentId}`}>Back to assignment details</Link>}
      />
      <ApplicationSurface className="av-assignment-runtime">
        <ApplicationSectionHeader
          title={targetLabel(runtime.assignment?.target)}
          description="Complete the presented activity below. Your response remains bound to this Assignment and presented stimulus."
        />
        {runtime.assignment?.instructions ? (
          <aside className="av-assignment-runtime__guidance" aria-label="Assignment instructions">
            <h3>Assignment instructions</h3>
            <p>{runtime.assignment.instructions}</p>
          </aside>
        ) : null}
      <TaskRuntimeProvider
        session={runtime.session}
        step={step}
        task={runtime.task}
        responses={runtime.responses || []}
        apiUrl={apiUrl}
        token={auth.user?.token}
        completeCurrentStep={completeCurrentStep}
        refetchProgramState={async () => undefined}
        backPath={`/app/assigned-work/${assignmentId}`}
        assignmentId={assignmentId}
        participantMediaContext={runtime.participantMediaContext}
      >
        <ToolComponent />
      </TaskRuntimeProvider>
      </ApplicationSurface>
    </ApplicationPage>
  );
}
